import { getScanCombinedInfos, getStcConfigByKey } from '../services/services.js'

/**
 * Fetches branding configuration using scan ID and API key
 * @param {string} scan_id - The scan ID
 * @param {string} api_key - The API key
 * @returns {Object|null} - The branding theme or null if not found
 */
const getBrandingConfiguration = async (scan_id, api_key) => {
  try {
    const res = await getScanCombinedInfos(scan_id, api_key)
    if (res) {
      const theme = res['auth-app-branding']
      setThemeColor(theme)
      return theme
    }
    return null
  } catch (error) {
    console.error('Error fetching branding configuration:', error)
    return null
  }
}

/**
 * Fetches branding configuration using a StcConfig key
 * @param {string} key - The StcConfig key
 * @returns {Object|null} - The branding theme or null if not found
 */
const getBrandingConfigurationByKey = async (key) => {
  try {
    const config = await getStcConfigByKey(key)
    if (config && config['published']) {
      const theme = config['published']['auth-app-branding']
      setThemeColor(theme)
      return theme
    }
    return null
  } catch (error) {
    console.error('Error fetching branding configuration by key:', error)
    return null
  }
}

/**
 * Sets CSS variables based on theme colors
 * @param {Object} theme - The theme object containing color values
 */
const setThemeColor = (theme) => {
  if (!theme || typeof theme !== 'object') return
  const root = document.documentElement

  Object.entries(theme).forEach(([key, value]) => {
    if (typeof value === 'string' && /^#([0-9A-F]{3}){1,2}$/i.test(value)) {
      root.style.setProperty(`--${key}`, value)
    }
  })

  if (theme?.background && isDarkColor(theme.background)) {
    root.style.setProperty('--text', '#ffffff')
  }
  if (theme?.primary && !isDarkColor(theme.primary)) {
    root.style.setProperty('--text-button', '#222222')
  }
}

const FONT_LOAD_TIMEOUT_MS = 1500
const loadedFontUrls = new Map()

const withTimeout = (promise, ms) =>
  Promise.race([promise, new Promise((resolve) => setTimeout(resolve, ms))])

/**
 * Loads a Google Fonts stylesheet. Rejects non-Google URLs to prevent
 * injecting arbitrary stylesheets. For China builds, rewrites the host
 * to the .cn mirror since fonts.googleapis.com is blocked by the GFW.
 * Returns a promise that resolves when the stylesheet has loaded (or failed).
 */
const loadGoogleFont = (cssUrl) => {
  if (!cssUrl) return Promise.resolve()
  if (loadedFontUrls.has(cssUrl)) return loadedFontUrls.get(cssUrl)

  let url
  try {
    url = new URL(cssUrl)
  } catch {
    return Promise.resolve()
  }
  if (
    url.hostname !== 'fonts.googleapis.com' ||
    !url.pathname.startsWith('/css2')
  ) {
    return Promise.resolve()
  }

  const mode = import.meta.env.MODE
  const isChina = mode === 'staging-china' || mode === 'production-china'
  const finalUrl = isChina
    ? cssUrl.replace('://fonts.googleapis.com/', '://fonts.googleapis.cn/')
    : cssUrl

  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = finalUrl

  const loaded = new Promise((resolve) => {
    link.onload = () => resolve()
    link.onerror = () => resolve()
  })
  document.head.appendChild(link)

  loadedFontUrls.set(cssUrl, loaded)
  return loaded
}

/**
 * Applies branding font for the given locale. Per-language overrides (theme.language_fonts[locale])
 * take precedence over the default (theme.font). Resets the CSS variable when no font is configured.
 * Awaits stylesheet + font-face load (capped by FONT_LOAD_TIMEOUT_MS) so callers can hide content
 * until the font is ready and avoid a flash of fallback text.
 * @param {Object} theme - The full branding theme
 * @param {string} locale - Active i18n locale (e.g. 'en', 'cs')
 */
const setBrandingFont = async (theme, locale) => {
  const root = document.documentElement
  if (!theme || typeof theme !== 'object') {
    root.style.removeProperty('--font-family')
    return
  }

  const font = theme.language_fonts?.[locale] || theme.default_font
  const fontFamily = font?.cssFamily || font?.family
  if (!fontFamily) {
    root.style.removeProperty('--font-family')
    return
  }

  const cssLoaded = loadGoogleFont(font.cssUrl)
  root.style.setProperty('--font-family', fontFamily)

  await withTimeout(
    cssLoaded
      .then(() => document.fonts?.load(`1em ${fontFamily}`))
      .catch(() => {
      }),
    FONT_LOAD_TIMEOUT_MS
  )
}

/**
 * Determines if a color is dark based on its luminance
 * @param {string} color - Hex color code (e.g., "#FFFFFF")
 * @returns {boolean} - True if the color is dark, false otherwise
 */
const isDarkColor = (color) => {
  const c = color.substring(1)
  const rgb = parseInt(c, 16)
  const r = (rgb >> 16) & 0xff
  const g = (rgb >> 8) & 0xff
  const b = (rgb >> 0) & 0xff
  const luma = 0.2126 * r + 0.7152 * g + 0.0722 * b
  return luma < 128
}

export {
  getBrandingConfigurationByKey,
  getBrandingConfiguration,
  setBrandingFont
}
